import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { ArrowLeft, ArrowRight, Filter } from 'lucide-react';
import QuestionCard from './QuestionCard';
import OptionButton from './OptionButton';

const PREFILTER_QUESTIONS = [
  {
    id: 'modele',
    title: 'Modèle principal',
    description: 'Quel est ton modèle d\'activité principal ?',
    options: [
      { value: 'conciergerie', label: 'Conciergerie' },
      { value: 'souslocation', label: 'Sous-location' },
      { value: 'mix', label: 'Mix' },
    ],
  },
  {
    id: 'logements',
    title: 'Nombre de logements aujourd\'hui',
    description: 'Combien de logements gères-tu actuellement ?',
    options: [
      { value: '0', label: '0' },
      { value: '1-2', label: '1 à 2' },
      { value: '3-4', label: '3 à 4' },
      { value: '5-9', label: '5 à 9' },
      { value: '10+', label: '10 ou plus' },
    ],
  },
  {
    id: 'chiffreAffaires',
    title: 'Chiffre d\'affaires mensuel (approx.)',
    description: 'Quel est ton CA mensuel approximatif ?',
    options: [
      { value: '0', label: '0' },
      { value: '<3k', label: 'Moins de 3 000 €' },
      { value: '3-10k', label: '3 000 à 10 000 €' },
      { value: '10k+', label: 'Plus de 10 000 €' },
    ],
  },
];

export default function PreFilterScreen({ onComplete, onBack }) {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});

  const question = PREFILTER_QUESTIONS[currentQuestion];
  const progress = ((currentQuestion + 1) / PREFILTER_QUESTIONS.length) * 100;
  const isLastQuestion = currentQuestion === PREFILTER_QUESTIONS.length - 1;
  const canProceed = answers[question.id] !== undefined;

  const handleSelect = (value) => {
    setAnswers(prev => ({ ...prev, [question.id]: value }));
  };

  const handleNext = () => {
    if (isLastQuestion) {
      onComplete(answers);
    } else {
      setCurrentQuestion(prev => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(prev => prev - 1);
    } else {
      onBack();
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      {/* Header with progress */}
      <header className="w-full border-b border-border bg-background sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Filter className="w-4 h-4" />
              <span>Pré-filtre rapide</span>
            </div>
            <span className="text-sm font-medium text-foreground">
              {currentQuestion + 1} / {PREFILTER_QUESTIONS.length}
            </span>
          </div>
          <Progress value={progress} className="h-1.5" />
        </div>
      </header>

      {/* Main content */}
      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="max-w-xl w-full animate-fade-in" key={currentQuestion}>
          <QuestionCard
            number={currentQuestion + 1}
            title={question.title}
            description={question.description}
          >
            <div className="space-y-3 mt-6">
              {question.options.map((option) => (
                <OptionButton
                  key={option.value}
                  label={option.label}
                  selected={answers[question.id] === option.value}
                  onClick={() => handleSelect(option.value)}
                />
              ))}
            </div>
          </QuestionCard>
        </div>
      </div>

      {/* Footer with navigation */}
      <footer className="w-full border-t border-border bg-background sticky bottom-0">
        <div className="max-w-2xl mx-auto px-6 py-4 flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={handlePrevious}
            className="gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Retour
          </Button>
          
          <Button
            onClick={handleNext}
            disabled={!canProceed}
            className="gap-2"
          >
            {isLastQuestion ? 'Continuer' : 'Suivant'}
            <ArrowRight className="w-4 h-4" />
          </Button>
        </div>
      </footer>
    </div>
  );
}